/**
 * usePlaidConnection — web implementation.
 *
 * Uses the Plaid Link JavaScript SDK loaded dynamically from CDN
 * (https://cdn.plaid.com/link/v2/stable/link-initialize.js).
 * This avoids Metro bundling issues with react-plaid-link on web.
 *
 * Handles two flows:
 *  1. Standard flow: create link token → open Plaid Link → onSuccess
 *  2. OAuth flow: after bank redirects back to /oauth/plaid, re-open
 *     Link with saved token + receivedRedirectUri to complete auth.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { usePathname } from 'expo-router';
import { trpc } from '../lib/trpc';
import { trackConnectionLinkEvent } from '../lib/product-events';
import { getPlaidRedirectUri } from '../lib/plaid-redirect';

const PLAID_LINK_URL = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
const LINK_TOKEN_KEY = 'plaid_link_token';

// ── Plaid Link JS SDK types ──────────────────────────────────────────────────

interface PlaidHandler {
  open: () => void;
  exit: (options?: { force: boolean }) => void;
  destroy: () => void;
}

interface PlaidSuccessMetadata {
  institution?: { institution_id?: string; name?: string } | null;
  link_session_id?: string;
  accounts?: unknown[];
}

interface PlaidExitError {
  display_message?: string | null;
  error_message?: string | null;
  error_code?: string | null;
  error_type?: string | null;
}

interface PlaidConfig {
  token: string;
  onSuccess: (publicToken: string, metadata: PlaidSuccessMetadata) => void;
  onExit?: (err: PlaidExitError | null, metadata?: unknown) => void;
  onEvent?: (eventName: string, metadata?: unknown) => void;
  receivedRedirectUri?: string;
}

declare global {
  interface Window {
    Plaid?: { create: (config: PlaidConfig) => PlaidHandler };
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function loadPlaidScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.Plaid) return Promise.resolve();
  if (document.querySelector(`script[src="${PLAID_LINK_URL}"]`)) {
    // Already loading — wait for it
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${PLAID_LINK_URL}"]`) as HTMLScriptElement;
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Plaid script failed to load')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PLAID_LINK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Plaid Link SDK'));
    document.head.appendChild(script);
  });
}

function getStringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function extractPlaidLinkMetadata(metadata: unknown) {
  if (!metadata || typeof metadata !== 'object') {
    return {
      linkSessionId: null,
      requestId: null,
      linkStatus: null,
      viewName: null,
      institutionId: null,
      institutionName: null,
      accountCount: null,
    };
  }

  const record = metadata as Record<string, unknown>;
  const institution =
    record['institution'] && typeof record['institution'] === 'object'
      ? (record['institution'] as Record<string, unknown>)
      : null;
  const accounts = Array.isArray(record['accounts']) ? record['accounts'] : null;

  return {
    linkSessionId:
      getStringValue(record['link_session_id']) ??
      getStringValue(record['linkSessionId']),
    requestId:
      getStringValue(record['request_id']) ?? getStringValue(record['requestId']),
    linkStatus: getStringValue(record['status']),
    viewName:
      getStringValue(record['view_name']) ?? getStringValue(record['viewName']),
    institutionId:
      getStringValue(institution?.['institution_id']) ??
      getStringValue(institution?.['id']),
    institutionName: getStringValue(institution?.['name']),
    accountCount: accounts ? accounts.length : null,
  };
}

function getErrorName(error: unknown): string | null {
  return error instanceof Error ? error.name : null;
}

function getErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') return null;
  const data = (error as { data?: { code?: unknown } }).data;
  return getStringValue(data?.code);
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface PlaidConnectionOptions {
  onSuccess: (connectionId: string) => void;
  onError?: (error: string) => void;
  /** Pass to trigger update mode (re-auth) for an existing connection. */
  connectionId?: string;
}

export function usePlaidConnection({
  onSuccess,
  onError,
  connectionId,
}: PlaidConnectionOptions) {
  const pathname = usePathname();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handlerRef = useRef<PlaidHandler | null>(null);

  const createLinkTokenMutation = trpc.connections.createLinkToken.useMutation();
  const authorizeMutation = trpc.connections.authorize.useMutation();

  // Check if we're returning from a Plaid OAuth redirect
  const isOAuthRedirect =
    typeof window !== 'undefined' &&
    window.location.search.includes('oauth_state_id=');

  // Build a Plaid handler for the given token (and optional OAuth redirect URI)
  const buildHandler = useCallback(
    async (token: string, receivedRedirectUri?: string): Promise<PlaidHandler> => {
      await loadPlaidScript();

      if (!window.Plaid) throw new Error('Plaid SDK not loaded');

      return window.Plaid.create({
        token,
        ...(receivedRedirectUri ? { receivedRedirectUri } : {}),

        onSuccess: async (publicToken, metadata) => {
          setIsLoading(true);
          const linkMetadata = extractPlaidLinkMetadata(metadata);
          try {
            const result = await authorizeMutation.mutateAsync({
              provider: 'plaid',
              publicToken,
              institutionId: metadata.institution?.institution_id ?? undefined,
              institutionName: metadata.institution?.name ?? undefined,
            });
            trackConnectionLinkEvent({
              route: pathname,
              screen: 'connections',
              provider: 'plaid',
              status: 'succeeded',
              metadata: {
                isUpdateMode: connectionId != null,
                ...linkMetadata,
                connectionId: result.connectionId,
              },
            });
            if (typeof sessionStorage !== 'undefined') {
              sessionStorage.removeItem(LINK_TOKEN_KEY);
            }
            onSuccess(result.connectionId);
          } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to connect account';
            trackConnectionLinkEvent({
              route: pathname,
              screen: 'connections',
              provider: 'plaid',
              status: 'failed',
              metadata: {
                isUpdateMode: connectionId != null,
                stage: 'authorize',
                ...linkMetadata,
                errorName: getErrorName(err),
                errorCode: getErrorCode(err),
                errorMessage: msg,
              },
            });
            setError(msg);
            onError?.(msg);
          } finally {
            setIsLoading(false);
          }
        },

        onExit: (err, metadata) => {
          setIsLoading(false);
          const linkMetadata = extractPlaidLinkMetadata(metadata);
          if (err) {
            const msg = err.display_message ?? err.error_message ?? 'Plaid Link exited with error';
            trackConnectionLinkEvent({
              route: pathname,
              screen: 'connections',
              provider: 'plaid',
              status: 'failed',
              metadata: {
                isUpdateMode: connectionId != null,
                stage: 'link_exit',
                ...linkMetadata,
                errorCode: err.error_code ?? null,
                errorType: err.error_type ?? null,
                displayMessage: err.display_message ?? null,
                errorMessage: msg,
              },
            });
            setError(msg);
            onError?.(msg);
            return;
          }

          trackConnectionLinkEvent({
            route: pathname,
            screen: 'connections',
            provider: 'plaid',
            status: 'exited',
            metadata: {
              isUpdateMode: connectionId != null,
              ...linkMetadata,
            },
          });
        },

        onEvent: (eventName, metadata) => {
          const linkMetadata = extractPlaidLinkMetadata(metadata);
          const eventMetadata =
            metadata && typeof metadata === 'object'
              ? (metadata as Record<string, unknown>)
              : {};

          trackConnectionLinkEvent({
            route: pathname,
            screen: 'connections',
            provider: 'plaid',
            status: 'event',
            metadata: {
              isUpdateMode: connectionId != null,
              providerEventName: eventName,
              ...linkMetadata,
              errorCode: getStringValue(eventMetadata['error_code']),
              errorType: getStringValue(eventMetadata['error_type']),
              errorMessage: getStringValue(eventMetadata['error_message']),
            },
          });
        },
      });
    },
    [authorizeMutation, connectionId, onSuccess, onError, pathname],
  );

  // On OAuth redirect: restore saved token and auto-open Link
  useEffect(() => {
    if (!isOAuthRedirect) return;

    const saved =
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem(LINK_TOKEN_KEY)
        : null;
    if (!saved) return;

    setIsLoading(true);
    buildHandler(saved, window.location.href)
      .then((handler) => {
        handlerRef.current = handler;
        handler.open();
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : 'Failed to re-open Plaid Link';
        trackConnectionLinkEvent({
          route: pathname,
          screen: 'connections',
          provider: 'plaid',
          status: 'failed',
          metadata: {
            isUpdateMode: connectionId != null,
            stage: 'oauth_resume',
            errorName: getErrorName(err),
            errorCode: getErrorCode(err),
            errorMessage: msg,
          },
        });
        setError(msg);
        onError?.(msg);
        setIsLoading(false);
      });
  }, [connectionId, isOAuthRedirect, buildHandler, onError, pathname]);

  // Cleanup handler on unmount
  useEffect(() => {
    return () => {
      handlerRef.current?.destroy();
    };
  }, []);

  const open = useCallback(async () => {
    setError(null);
    setIsLoading(true);
    trackConnectionLinkEvent({
      route: pathname,
      screen: 'connections',
      provider: 'plaid',
      status: 'started',
      metadata: {
        isUpdateMode: connectionId != null,
      },
    });

    // Destroy any existing handler before creating a new one
    handlerRef.current?.destroy();
    handlerRef.current = null;

    try {
      const redirectUri = getPlaidRedirectUri();

      const result = await createLinkTokenMutation.mutateAsync({
        provider: 'plaid',
        redirectUri,
        ...(connectionId ? { connectionId } : {}),
      });

      if (!result.linkToken) throw new Error('No link token returned from server');

      // Save for OAuth redirect recovery
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(LINK_TOKEN_KEY, result.linkToken);
      }

      const handler = await buildHandler(result.linkToken);
      handlerRef.current = handler;
      handler.open();
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'plaid',
        status: 'opened',
        metadata: {
          isUpdateMode: connectionId != null,
        },
      });
      // isLoading stays true until onSuccess or onExit fires
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to open Plaid Link';
      trackConnectionLinkEvent({
        route: pathname,
        screen: 'connections',
        provider: 'plaid',
        status: 'failed',
        metadata: {
          isUpdateMode: connectionId != null,
          stage: 'open',
          errorMessage: msg,
        },
      });
      setError(msg);
      onError?.(msg);
      setIsLoading(false);
    }
  }, [buildHandler, connectionId, createLinkTokenMutation, onError, pathname]);

  return {
    open,
    isLoading: isLoading || createLinkTokenMutation.isPending || authorizeMutation.isPending,
    error,
    isOAuthRedirect,
  };
}
